/**
 * Traduction d'un refus **serveur** en message joueur (M2-07, DECISIONS §9.4).
 *
 * Le serveur répond des codes fermés (`ERROR_CODES`) et, pour certains refus métier, un
 * `details.raison` plus précis. Un même code peut donc mériter deux messages : `INSUFFICIENT_ECLATS`
 * avec `details.ressource = 'jokers'` signifie « plus de joker », pas « pas assez d'éclats ».
 *
 * Ce module ne devine rien : sans détail reconnu, il retombe sur `ui:errors.<CODE>`, la traduction
 * générique du contrat. Aucun message serveur brut n'est jamais affiché.
 */
import { cleI18nErreur } from '../api/erreurs';

import type { ErreurApi } from '../api/erreurs';

/** Vrai si le code est celui d'un refus que le joueur peut comprendre et corriger. */
function estRefusMetier(erreur: ErreurApi): boolean {
  return [
    'INSUFFICIENT_ECLATS',
    'VALIDATION_FAILED',
    'WAVE_ALREADY_CLOSED',
    'CARD_NOT_AVAILABLE',
    'UPGRADE_MAX_LEVEL',
    'RUN_INCOHERENT',
  ].includes(erreur.code);
}

/**
 * Clé i18n à afficher pour un refus, ou `null` si l'erreur ne doit rien afficher.
 *
 * `401` renvoie `null` : la session est purgée par le client HTTP et la garde d'accès redirige
 * vers `/connexion` — afficher un message de refus n'aurait pas de sens.
 */
export function cleI18nRefus(erreur: ErreurApi): string | null {
  if (erreur.statutHttp === 401) return null;
  if (!estRefusMetier(erreur)) return cleI18nErreur(erreur);

  const details = erreur.details ?? {};
  const raison = typeof details['raison'] === 'string' ? details['raison'] : null;
  const ressource = typeof details['ressource'] === 'string' ? details['ressource'] : null;
  const champ = typeof details['champ'] === 'string' ? details['champ'] : null;

  if (erreur.code === 'INSUFFICIENT_ECLATS' && ressource === 'jokers') {
    return 'jeu.refus.jokers-epuises';
  }
  if (erreur.code === 'INSUFFICIENT_ECLATS') return 'jeu.refus.eclats-insuffisants';
  if (erreur.code === 'UPGRADE_MAX_LEVEL') return 'jeu.refus.amelioration-maximum';

  if (erreur.code === 'WAVE_ALREADY_CLOSED' && raison === 'choix_deja_effectue') {
    return 'jeu.refus.choix-deja-effectue';
  }
  if (erreur.code === 'WAVE_ALREADY_CLOSED') return cleI18nErreur(erreur);
  if (erreur.code === 'CARD_NOT_AVAILABLE') return 'jeu.refus.carte-indisponible';

  if (erreur.code === 'RUN_INCOHERENT') {
    if (raison === 'amelioration_pendant_vague') return 'jeu.refus.amelioration-pendant-vague';
    if (raison === 'joker_requis_pendant_vague') return 'jeu.refus.joker-requis';
    return cleI18nErreur(erreur);
  }

  // `VALIDATION_FAILED` : le serveur précise la ressource ou le champ fautif.
  if (raison === 'emplacement_occupe' || champ === 'emplacementId') {
    return raison === 'emplacement_occupe'
      ? 'jeu.refus.emplacement-occupe'
      : 'jeu.refus.emplacement-inconnu';
  }
  if (champ === 'towerId') return 'jeu.refus.tour-inconnue';
  return cleI18nErreur(erreur);
}
